import {
  ShowBase,
  useShowContext,
  useRecordContext,
  useGetList,
  useGetOne,
  useUpdate,
  useCreate,
  useRefresh,
  useGetIdentity,
  useDelete,
  useNotify,
} from "ra-core";
import { Link, useNavigate } from "react-router";
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Ruler,
  DollarSign,
  Activity as ActivityIcon,
  FileText,
  Receipt,
  MessageSquare,
  ClipboardList,
  Plus,
  Save,
  Pencil,
  X,
  PhoneCall,
  FileEdit,
  Home,
  RefreshCw,
  User,
  UserCircle,
  Trash2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ReferenceField } from "@/components/admin/reference-field";
import { useState } from "react";

import type {
  Job,
  Activity,
  Proposal,
  Invoice,
  ActivityType,
  Sale,
} from "../../providers/fakerest/dataGenerator/types";
import { JOB_STAGES, findStageLabel, findStageColor } from "./jobStages";
import {
  JOB_TYPES,
  getJobTypeLabel,
  getJobTypeBadgeClass,
  getJobTypeIcon,
} from "../constants";

export const JobShow = () => {
  return (
    <ShowBase>
      <JobShowContent />
    </ShowBase>
  );
};

const JobShowContent = () => {
  const { record, isPending } = useShowContext<Job>();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const navigate = useNavigate();
  const notify = useNotify();

  const handleDelete = () => {
    deleteOne(
      "jobs",
      { id: record!.id, previousData: record },
      {
        onSuccess: () => {
          notify("Job deleted successfully", { type: "success" });
          navigate("/jobs");
        },
        onError: () => {
          notify("Failed to delete job", { type: "error" });
        },
      },
    );
  };

  if (isPending || !record) return null;

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Back button and delete */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/jobs"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Jobs
        </Link>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm" disabled={isDeleting}>
              <Trash2 className="h-4 w-4 mr-1" />
              Delete Job
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Job</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this job? This action cannot be
                undone and will also delete all associated activities.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Contact Header */}
      <JobContactHeader job={record} />

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Left: Job Details */}
        <JobDetails job={record} />

        {/* Right: Activity Timeline */}
        <JobActivityTimeline jobId={record.id} />
      </div>

      {/* Bottom: Collapsible sections */}
      <div className="mt-6">
        <Accordion type="multiple" className="w-full">
          <JobProposals jobId={record.id} />
          <JobInvoices jobId={record.id} />
        </Accordion>
      </div>
    </div>
  );
};

// Contact Header Component
const JobContactHeader = ({ job }: { job: Job }) => {
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.address)}`;
  // Google Maps embed URL (satellite view)
  const googleMapsEmbedUrl = `https://maps.google.com/maps?q=${encodeURIComponent(job.address)}&t=k&z=19&output=embed`;

  return (
    <Card className="py-0 gap-0">
      <CardContent className="p-4">
        {/* Contact Info */}
        <ReferenceField source="contact_id" reference="contacts" link={false}>
          <ContactInfo address={job.address} mapsUrl={googleMapsUrl} />
        </ReferenceField>

        {/* Map Embed - Below contact info */}
        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-4 w-full h-48 rounded-lg overflow-hidden border hover:opacity-90 transition-opacity"
        >
          <iframe
            src={googleMapsEmbedUrl}
            width="100%"
            height="100%"
            style={{ border: 0, pointerEvents: "none" }}
            loading="lazy"
            title="Property satellite view"
          />
        </a>
      </CardContent>
    </Card>
  );
};

const ContactInfo = ({
  address,
  mapsUrl,
}: {
  address: string;
  mapsUrl: string;
}) => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <div>
      <Link
        to={`/contacts/${record.id}/show`}
        className="text-xl font-semibold mb-3 hover:text-blue-600 hover:underline inline-block"
      >
        {record.name}
      </Link>
      <div className="flex flex-wrap gap-4 text-sm">
        {record.phone && (
          <a
            href={`tel:${record.phone}`}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <Phone className="h-4 w-4" />
            {record.phone}
          </a>
        )}
        {record.email && (
          <a
            href={`mailto:${record.email}`}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <Mail className="h-4 w-4" />
            {record.email}
          </a>
        )}
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-muted-foreground hover:text-blue-600"
        >
          <MapPin className="h-4 w-4" />
          {address}
        </a>
      </div>
    </div>
  );
};

// Job Details Component with inline editing
const JobDetails = ({ job }: { job: Job }) => {
  const [update, { isPending: isUpdating }] = useUpdate();
  const refresh = useRefresh();
  const [isEditing, setIsEditing] = useState(false);

  // Get all sales users for the dropdown
  const { data: salesUsers } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  // Get the assigned user
  const { data: assignedUser } = useGetOne<Sale>("sales", {
    id: job.assigned_to,
  });

  // Editable fields
  const [jobType, setJobType] = useState(job.job_type);
  const [roofSqft, setRoofSqft] = useState(String(job.roof_sqft));
  const [estimatedValue, setEstimatedValue] = useState(
    String(job.estimated_value),
  );
  const [status, setStatus] = useState(job.status);
  const [assignedTo, setAssignedTo] = useState(String(job.assigned_to));
  const [notes, setNotes] = useState(job.notes || "");

  const hasChanges =
    jobType !== job.job_type ||
    Number(roofSqft) !== job.roof_sqft ||
    Number(estimatedValue) !== job.estimated_value ||
    status !== job.status ||
    Number(assignedTo) !== job.assigned_to ||
    notes !== (job.notes || "");

  const handleSave = () => {
    update(
      "jobs",
      {
        id: job.id,
        data: {
          job_type: jobType,
          roof_sqft: Number(roofSqft) || 0,
          estimated_value: Number(estimatedValue) || 0,
          status,
          assigned_to: Number(assignedTo),
          notes: notes.trim(),
        },
        previousData: job,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          refresh();
        },
      },
    );
  };

  const handleCancel = () => {
    setJobType(job.job_type);
    setRoofSqft(String(job.roof_sqft));
    setEstimatedValue(String(job.estimated_value));
    setStatus(job.status);
    setAssignedTo(String(job.assigned_to));
    setNotes(job.notes || "");
    setIsEditing(false);
  };

  const handleStatusChange = (newStatus: string) => {
    if (!isEditing) {
      // Direct status change without edit mode
      update(
        "jobs",
        { id: job.id, data: { status: newStatus }, previousData: job },
        { onSuccess: () => refresh() },
      );
    } else {
      setStatus(newStatus);
    }
  };

  const JobTypeIcon = getJobTypeIcon(job.job_type);
  const EditJobTypeIcon = getJobTypeIcon(jobType);

  const formattedValue = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(job.estimated_value);

  const formattedSqft = new Intl.NumberFormat("en-US").format(job.roof_sqft);

  if (isEditing) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Edit Job Details
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isUpdating || !hasChanges}
              >
                <Save className="h-4 w-4 mr-1" />
                {isUpdating ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Job Type */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <EditJobTypeIcon className="h-4 w-4" />
              Type
            </Label>
            <Select value={jobType} onValueChange={setJobType}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {JOB_TYPES.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <span className="flex items-center gap-2">
                      <type.icon className={`h-4 w-4 ${type.color}`} />
                      {type.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Roof Sq Ft */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Ruler className="h-4 w-4" />
              Roof Sq Ft
            </Label>
            <Input
              type="number"
              value={roofSqft}
              onChange={(e) => setRoofSqft(e.target.value)}
              className="w-48"
            />
          </div>

          {/* Estimated Value */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <DollarSign className="h-4 w-4" />
              Estimated Value
            </Label>
            <Input
              type="number"
              value={estimatedValue}
              onChange={(e) => setEstimatedValue(e.target.value)}
              className="w-48"
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <ActivityIcon className="h-4 w-4" />
              Status
            </Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {JOB_STAGES.map((stage) => (
                  <SelectItem key={stage.value} value={stage.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${stage.color}`} />
                      {stage.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Assigned To */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <UserCircle className="h-4 w-4" />
              Assigned To
            </Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {salesUsers
                  ?.filter((u) => !u.disabled)
                  .map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      <div className="flex items-center gap-2">
                        {user.avatar?.src ? (
                          <img
                            src={user.avatar.src}
                            alt=""
                            className="w-5 h-5 rounded-full"
                          />
                        ) : (
                          <User className="w-5 h-5 text-muted-foreground" />
                        )}
                        {user.first_name} {user.last_name}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <MessageSquare className="h-4 w-4" />
              Notes
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about the job..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Job Details
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground flex items-center gap-1.5">
            <JobTypeIcon className="h-4 w-4" />
            Type
          </span>
          <Badge
            className={`${getJobTypeBadgeClass(job.job_type)} flex items-center gap-1.5`}
          >
            <JobTypeIcon className="h-3 w-3" />
            {getJobTypeLabel(job.job_type)}
          </Badge>
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground flex items-center gap-1.5">
            <Ruler className="h-4 w-4" />
            Roof Sq Ft
          </span>
          <span className="font-medium">{formattedSqft}</span>
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground flex items-center gap-1.5">
            <DollarSign className="h-4 w-4" />
            Estimated Value
          </span>
          <span className="font-semibold text-lg">{formattedValue}</span>
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground flex items-center gap-1.5">
            <ActivityIcon className="h-4 w-4" />
            Status
          </span>
          <Select value={job.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {JOB_STAGES.map((stage) => (
                <SelectItem key={stage.value} value={stage.value}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${stage.color}`} />
                    {stage.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground flex items-center gap-1.5">
            <UserCircle className="h-4 w-4" />
            Assigned To
          </span>
          {assignedUser ? (
            <Link
              to={`/sales/${assignedUser.id}/show`}
              className="flex items-center gap-2 hover:text-primary transition-colors"
            >
              {assignedUser.avatar?.src ? (
                <img
                  src={assignedUser.avatar.src}
                  alt=""
                  className="w-6 h-6 rounded-full"
                />
              ) : (
                <User className="w-6 h-6 text-muted-foreground" />
              )}
              <span className="font-medium hover:underline">
                {assignedUser.first_name} {assignedUser.last_name}
              </span>
            </Link>
          ) : (
            <div className="flex items-center gap-2">
              <User className="w-6 h-6 text-muted-foreground" />
              <span className="font-medium">Unassigned</span>
            </div>
          )}
        </div>

        {job.notes && (
          <>
            <Separator />
            <div>
              <span className="text-sm text-muted-foreground flex items-center gap-1.5 mb-2">
                <MessageSquare className="h-4 w-4" />
                Notes
              </span>
              <p className="text-sm">{job.notes}</p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Activity type configuration
const ACTIVITY_TYPES: {
  value: ActivityType;
  label: string;
  icon: typeof PhoneCall;
  color: string;
}[] = [
  {
    value: "call",
    label: "Phone Call",
    icon: PhoneCall,
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  },
  {
    value: "email",
    label: "Email",
    icon: Mail,
    color:
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  },
  {
    value: "note",
    label: "Note",
    icon: FileEdit,
    color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  },
  {
    value: "site_visit",
    label: "Site Visit",
    icon: Home,
    color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  },
  {
    value: "status_change",
    label: "Status Change",
    icon: RefreshCw,
    color:
      "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  },
];

// Activity Timeline Component with all 5 types
const JobActivityTimeline = ({ jobId }: { jobId: number }) => {
  const [newDescription, setNewDescription] = useState("");
  const [activityType, setActivityType] = useState<ActivityType>("note");
  const [create, { isPending: isCreating }] = useCreate();
  const refresh = useRefresh();
  const { data: identity } = useGetIdentity();

  const { data: activities, isPending } = useGetList<Activity>("activities", {
    filter: { job_id: jobId },
    sort: { field: "timestamp", order: "DESC" },
    pagination: { page: 1, perPage: 50 },
  });

  // Get all sales users to display who performed each activity
  const { data: salesUsers } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
  });

  const getUserById = (userId: number) =>
    salesUsers?.find((u) => u.id === userId);

  const handleAddActivity = () => {
    if (!newDescription.trim()) return;

    // Get current user ID from identity
    const currentUserId = (identity as Sale | undefined)?.id ?? 1;

    create(
      "activities",
      {
        data: {
          job_id: jobId,
          user_id: currentUserId,
          type: activityType,
          description: newDescription.trim(),
          timestamp: new Date().toISOString(),
        },
      },
      {
        onSuccess: () => {
          setNewDescription("");
          refresh();
        },
      },
    );
  };

  const typeColors: Record<string, string> = {
    call: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    email:
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    note: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
    site_visit:
      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    status_change:
      "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  };

  const getTypeIcon = (type: string) => {
    const config = ACTIVITY_TYPES.find((t) => t.value === type);
    return config?.icon || FileEdit;
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ActivityIcon className="h-5 w-5" />
          Activity Timeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Add activity input */}
        <div className="space-y-3 mb-4">
          <Textarea
            placeholder="Add a note or activity..."
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            rows={3}
            className="resize-none"
          />
          <div className="flex items-center justify-between">
            <Select
              value={activityType}
              onValueChange={(value) => setActivityType(value as ActivityType)}
            >
              <SelectTrigger className="w-44">
                <SelectValue>
                  {(() => {
                    const selected = ACTIVITY_TYPES.find(
                      (t) => t.value === activityType,
                    );
                    if (!selected) return null;
                    const Icon = selected.icon;
                    return (
                      <Badge
                        className={`${selected.color} flex items-center gap-1`}
                      >
                        <Icon className="h-3 w-3" />
                        {selected.label}
                      </Badge>
                    );
                  })()}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <Badge className={`${type.color} flex items-center gap-1`}>
                      <type.icon className="h-3 w-3" />
                      {type.label}
                    </Badge>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleAddActivity}
              disabled={isCreating || !newDescription.trim()}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </div>

        {/* Timeline */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {isPending ? (
            <p className="text-muted-foreground text-sm">Loading...</p>
          ) : activities && activities.length > 0 ? (
            activities.map((activity) => {
              const TypeIcon = getTypeIcon(activity.type);
              const activityUser = getUserById(activity.user_id);
              return (
                <div
                  key={activity.id}
                  className="flex gap-3 p-3 rounded-lg bg-muted/50"
                >
                  {/* User avatar */}
                  {activityUser?.avatar?.src ? (
                    <img
                      src={activityUser.avatar.src}
                      alt=""
                      className="w-8 h-8 rounded-full shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <User className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-medium">
                        {activityUser
                          ? `${activityUser.first_name} ${activityUser.last_name}`
                          : "Unknown"}
                      </span>
                      <Badge
                        variant="secondary"
                        className={`${typeColors[activity.type]} flex items-center gap-1`}
                      >
                        <TypeIcon className="h-3 w-3" />
                        {activity.type.replace(/_/g, " ")}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(activity.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm">{activity.description}</p>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-muted-foreground text-sm text-center py-4">
              No activities yet
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Proposals Accordion Section
const JobProposals = ({ jobId }: { jobId: number }) => {
  const navigate = useNavigate();
  const { data: proposals, isPending } = useGetList<Proposal>("proposals", {
    filter: { job_id: jobId },
    sort: { field: "created_at", order: "DESC" },
    pagination: { page: 1, perPage: 20 },
  });

  const statusColors: Record<string, string> = {
    draft: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
    sent: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    accepted:
      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    declined: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);

  const count = proposals?.length ?? 0;

  return (
    <AccordionItem value="proposals">
      <AccordionTrigger>
        <span className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Proposals ({isPending ? "..." : count})
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/proposals/create?job_id=${jobId}`)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Create Proposal
          </Button>
        </div>
        {isPending ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : proposals && proposals.length > 0 ? (
          <div className="space-y-2">
            {proposals.map((proposal) => (
              <Link
                key={proposal.id}
                to={`/proposals/${proposal.id}/show`}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Badge className={statusColors[proposal.status]}>
                    {proposal.status}
                  </Badge>
                  <span className="font-medium">
                    {formatCurrency(proposal.total)}
                  </span>
                </div>
                <span className="text-sm text-muted-foreground">
                  Valid until: {proposal.valid_until}
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No proposals yet</p>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

// Invoices Accordion Section
const JobInvoices = ({ jobId }: { jobId: number }) => {
  const navigate = useNavigate();
  const { data: invoices, isPending } = useGetList<Invoice>("invoices", {
    filter: { job_id: jobId },
    sort: { field: "due_date", order: "DESC" },
    pagination: { page: 1, perPage: 20 },
  });

  const statusColors: Record<string, string> = {
    pending:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    paid: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    overdue: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);

  const count = invoices?.length ?? 0;

  return (
    <AccordionItem value="invoices">
      <AccordionTrigger>
        <span className="flex items-center gap-2">
          <Receipt className="h-4 w-4" />
          Invoices ({isPending ? "..." : count})
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/invoices/create?job_id=${jobId}`)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Create Invoice
          </Button>
        </div>
        {isPending ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : invoices && invoices.length > 0 ? (
          <div className="space-y-2">
            {invoices.map((invoice) => (
              <Link
                key={invoice.id}
                to={`/invoices/${invoice.id}/show`}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Badge className={statusColors[invoice.status]}>
                    {invoice.status}
                  </Badge>
                  <span className="font-medium">
                    {formatCurrency(invoice.amount)}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  <span>Due: {invoice.due_date}</span>
                  {invoice.paid_at && (
                    <span className="ml-3 text-green-600 dark:text-green-400">
                      Paid: {new Date(invoice.paid_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No invoices yet</p>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};
